/**
 * AppRouter Component - Main application routing with DISABLED ONBOARDING
 * 
 * ONBOARDING SYSTEM STATUS: DISABLED (January 2025)
 * - Users enter directly to Dashboard screen without onboarding flow
 * - All onboarding code preserved for future re-enablement
 * 
 * TO RE-ENABLE ONBOARDING:
 * 1. Revert onboardingSlice.ts initialState: isCompleted: false, completedSteps: [], completionTime: null
 * 2. Revert navigationSlice.ts initialState: currentScreen: 'onboarding', navigationHistory: ['onboarding']
 * 3. Restore the useEffect onboarding validation logic in this file (see line 43-48)
 * 
 * PRESERVED FILES:
 * - All screens/onboarding/ directory files
 * - All onboarding test files
 * - All onboarding Redux actions and reducers
 */

import { FC, useEffect, lazy, Suspense } from 'react';
import { useAppSelector, useAppDispatch } from '../hooks/redux';
import { Screen } from '../store/slices/navigationSlice';
import { selectIsOnboardingCompleted } from '../store/slices/onboardingSlice';
import { useDirectusAuth } from '../providers/DirectusAuthProvider';
import { useLanguage } from '../contexts/LanguageContext';

// Critical path imports - needed immediately
import { Dashboard } from '../screens/Dashboard';
import { AuthScreen } from '../screens/AuthScreen';

// Lazy-loaded screens (not needed on initial render)
const CustomerSelection = lazy(() => import('../screens/CustomerSelection').then(m => ({ default: m.CustomerSelection })));
const ActiveVisit = lazy(() => import('../screens/ActiveVisit').then(m => ({ default: m.ActiveVisit })));
const EvidenceCapture = lazy(() => import('../screens/EvidenceCapture').then(m => ({ default: m.EvidenceCapture })));
const VisitSummary = lazy(() => import('../screens/VisitSummary').then(m => ({ default: m.VisitSummary })));
const Profile = lazy(() => import('../screens/Profile').then(m => ({ default: m.Profile })));
const PhoneScreen = lazy(() => import('../screens/Phone').then(m => ({ default: m.PhoneScreen })));
const Onboarding = lazy(() => import('../screens/onboarding/Onboarding').then(m => ({ default: m.Onboarding })));
const Reports = lazy(() => import('../screens/Reports').then(m => ({ default: m.Reports })));
const ContextSales = lazy(() => import('../screens/AllCustomers').then(m => ({ default: m.AllCustomers })));
const Interactions = lazy(() => import('../screens/Interactions').then(m => ({ default: m.Interactions })));
const Products = lazy(() => import('../screens/Products').then(m => ({ default: m.Products })));
const HelpScreen = lazy(() => import('../screens/HelpScreen').then(m => ({ default: m.HelpScreen })));

// Lazy loading fallback component
const ScreenLoadingFallback: FC = () => {
  const { t } = useLanguage();
  return (
    <div className="flex h-screen">
      <div className="m-auto p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground text-sm">{t('common.status.loading')}</p>
      </div>
    </div>
  );
};



export const AppRouter: FC = () => {
  const { t } = useLanguage();
  const dispatch = useAppDispatch();
  const currentScreen = useAppSelector((state) => state.navigation.currentScreen);
  const isLoading = useAppSelector((state) => state.navigation.isLoading);
  const isOnboardingCompleted = useAppSelector(selectIsOnboardingCompleted);
  const { isAuthenticated, isLoading: authLoading } = useDirectusAuth();

  // ONBOARDING DISABLED: Simplified navigation without onboarding checks
  // Original onboarding validation logic has been disabled to streamline user experience
  // To re-enable: restore the useEffect with onboarding completion checks
  useEffect(() => {
    // Debug logging for troubleshooting
    console.log('🧭 AppRouter: Navigation state check', { 
      currentScreen, 
      isAuthenticated, 
      isOnboardingCompleted 
    });
    
    // No onboarding validation - users can navigate freely to any screen
    // All navigation is handled through Redux actions without restrictions
  }, [currentScreen, isOnboardingCompleted, dispatch, isAuthenticated]);

  // Show loading while checking authentication
  if (authLoading || isLoading) {
    return (
      <div className="flex h-screen">
        <div className="m-auto p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">{t('common.status.loading')}</p>
        </div>
      </div>
    );
  }

  // Show auth screen if not authenticated
  if (!isAuthenticated) {
    return <AuthScreen />;
  }

  const renderScreen = (): React.JSX.Element => {
    // Dashboard is eagerly loaded - no Suspense needed
    if (currentScreen === 'dashboard') {
      return <Dashboard />;
    }

    // All other screens are lazy loaded
    return (
      <Suspense fallback={<ScreenLoadingFallback />}>
        {(() => {
          switch (currentScreen as Screen) {
            case 'onboarding':
              return <Onboarding />;
            case 'customerSelect':
              return <CustomerSelection />;
            case 'visitActive':
              return <ActiveVisit />;
            case 'evidenceCapture':
              return <EvidenceCapture />;
            case 'visitSummary':
              return <VisitSummary />;
            case 'reports':
              return <Reports />;
            case 'contextSales':
              return <ContextSales />;
            case 'interactions':
              return <Interactions />;
            case 'products':
              return <Products />;
            case 'help':
              return <HelpScreen />;
            case 'profile':
              return <Profile />;
            case 'Phone':
              return <PhoneScreen />;
            default:
              return <Dashboard />;
          }
        })()}
      </Suspense>
    );
  };

  return (
    <div className="flex flex-col h-screen bg-background">
      {renderScreen()}
    </div>
  );
};